import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Theme,
  useMediaQuery,
  IconButton
} from "@mui/material";
import { fetchApi } from "../services/api";
import SideMenu from "../components/SideMenu";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CloseIcon from "@mui/icons-material/Close";
import AlertSnackbar from "../components/AlertSnackbar";

interface ApiTableResponse {
  table: Employee[];
}

interface Employee {
  아이디: string;
  프로필: string;
  회원번호: number;
  유저번호: number;
  업체번호: number;
  부서번호?: number;
  부서명: string;
  유저명: string;
  직책명: string;
  직책코드: string;
  관리자여부: boolean;
  수납관리자여부: boolean;
  정산관리자여부: boolean;
  통장조회가능여부: boolean;
  발권담당자여부: boolean;
  상태코드: string;
  상태: string;
  대표이메일: string;
  국가번호: string;
  대표전화: string;
  영문성: string;
  영문이름: string;
  입사일: string | null;
  생년월일: string | null;
  외부콜센터여부: boolean;
  성별: string;
  성별코드: string;
  기본주소: string;
  상세주소: string;
  우편번호: string;
  재직여부: boolean;
  관리자여부1: boolean;
  알림수신: boolean;
  
}

interface EmployeeResponse {
  data: {
    table: Employee[];
  };
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  "&:hover": {
    cursor: "pointer",
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  marginBottom: theme.spacing(2),
}));

const drawerWidth = 240;

const EmployeeManagementPage = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordChangeDialogOpen, setPasswordChangeDialogOpen] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    userNo: 0
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchEmployees = async () => {
    try {
      const CompanyNo = localStorage.getItem("CompanyNo") || "1";
      const postData = [
        {
          컬럼명: "@업체번호",
          값: CompanyNo,
        },
        {
          컬럼명: "@상태",
          값: "208002",
          리스트값: null,
        },
      ];

      const response = await fetchApi<ApiTableResponse>(
        "DBO.CW_S_사원",
        postData
      );
      setEmployees(response.data.table);
    } catch (err) {
      setError("직원 정보를 불러오는데 실패했습니다.");
      console.error("Error fetching employees:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeClick = async (employee: Employee) => {
    try {
      const postData = [
        {
          컬럼명: "@업체번호",
          값: employee.업체번호.toString(),
        },
        {
          컬럼명: "@유저번호",
          값: employee.유저번호.toString(),
        },
      ];

      const response = await fetchApi<ApiTableResponse>(
        "DBO.CW_S_사원정보",
        postData
      );
      if (response.data.table && response.data.table.length > 0) {
        const employeeDetails = response.data.table[0];
        setSelectedEmployee({
          ...employee,
          ...employeeDetails,
          유저명: employeeDetails.유저명 || employee.유저명,
          대표이메일: employeeDetails.대표이메일 || employee.대표이메일,
          대표전화: employeeDetails.대표전화 || employee.대표전화,
        });
        setModalOpen(true);
      }
    } catch (err) {
      console.error("Error fetching employee details:", err);
      setError("직원 상세 정보를 불러오는데 실패했습니다.");
    }
  };

  const handleSave = async () => {
    if (!selectedEmployee) return;

    setSaving(true);
    try {
      const isNewEmployee = selectedEmployee.유저번호 === 0;
      const procedureName = isNewEmployee
        ? "DBO.CW_T_사원등록"
        : "DBO.CW_T_사원수정";
      const CompanyNo = localStorage.getItem("CompanyNo") || "1";

      const postData = [
        {
          컬럼명: "@업체번호",
          값: isNewEmployee ? CompanyNo : selectedEmployee.업체번호.toString(),
        },
        {
          컬럼명: "@유저번호",
          값: selectedEmployee.유저번호.toString(),
        },
        {
          컬럼명: "@재직여부",
          값: selectedEmployee.재직여부.toString(),
        },
        {
          컬럼명: "@알림수신",
          값: selectedEmployee.알림수신.toString(),
        },
        {
          컬럼명: "@아이디",
          값: selectedEmployee.아이디,
        },
        {
          컬럼명: "@한글이름",
          값: selectedEmployee.유저명,
        },
        {
          컬럼명: "@영문성",
          값: selectedEmployee.영문성 || "",
        },
        {
          컬럼명: "@영문이름",
          값: selectedEmployee.영문이름 || "",
        },
        {
          컬럼명: "@이메일",
          값: selectedEmployee.대표이메일,
        },
        {
          컬럼명: "@휴대폰",
          값: selectedEmployee.대표전화,
        },
        {
          컬럼명: "@부서명",
          값: selectedEmployee.부서명,
        },
        {
          컬럼명: "@부서번호",
          값: (selectedEmployee.부서번호 || "").toString(),
        },
        {
          컬럼명: "@직책명",
          값: selectedEmployee.직책명 || "",
        },
        {
          컬럼명: "@입사일",
          값: selectedEmployee.입사일 || "NULL",
        },
        {
          컬럼명: "@생일",
          값: selectedEmployee.생년월일 || "",
        },
        {
          컬럼명: "@우편번호",
          값: selectedEmployee.우편번호 || "",
        },
        {
          컬럼명: "@기본주소",
          값: selectedEmployee.기본주소 || "",
        },
        {
          컬럼명: "@상세주소",
          값: selectedEmployee.상세주소 || "",
        },
        {
          컬럼명: "@성별코드",
          값: selectedEmployee.성별코드,
        },
        {
          컬럼명: "@변경자",
          값: localStorage.getItem("UserNo") || "",
        },
        {
          컬럼명: "@프로필",
          값: selectedEmployee.프로필 || "",
        },
      ];

      await fetchApi(procedureName, postData);
      setSnackbar({
        open: true,
        message: "저장이 완료되었습니다.",
        severity: "success",
      });
      handleModalClose();
      fetchEmployees();
    } catch (err) {
      console.error("Error saving employee:", err);
      setSnackbar({
        open: true,
        message: "저장 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEmployeeChange = (field: keyof Employee, value: any) => {
    if (selectedEmployee) {
      setSelectedEmployee({
        ...selectedEmployee,
        [field]: value,
      });
    }
  };

  const handlePasswordChange = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      setSnackbar({
        open: true,
        message: '변경할 비밀번호를 재확인 해주세요',
        severity: 'error'
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(
        `https://trpliveapi1.tripbox.co.kr/ERP/ChangePassword?userNo=${passwordChangeData.userNo}&oldPassword=${passwordChangeData.oldPassword}&userPassword=${passwordChangeData.newPassword}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      const data = await response.json();
      
      if (data.message === '이전 비밀번호가 잘못되었습니다') {
        setSnackbar({
          open: true,
          message: data.message,
          severity: 'error'
        });
      } else if (data === 'success') {
        setSnackbar({
          open: true,
          message: '비밀번호 변경 완료',
          severity: 'success'
        });
        setPasswordChangeDialogOpen(false);
        setPasswordChangeData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
          userNo: 0
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: '비밀번호 변경 중 오류가 발생했습니다',
        severity: 'error'
      });
    }
  };

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEmployee(null);
  };

  const isFormValid = () => {
    if (!selectedEmployee) return false;
    return (
      selectedEmployee.아이디?.trim() !== '' &&
      selectedEmployee.유저명?.trim() !== '' &&
      selectedEmployee.대표이메일?.trim() !== '' &&
      selectedEmployee.대표전화?.trim() !== ''
    );
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ display: "flex" }}>
      <SideMenu
        mobileOpen={mobileOpen}
        handleDrawerToggle={() => setMobileOpen(!mobileOpen)}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          p: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...(isMobile
              ? { p: 1.5, mt: 6 }
              : { py: 2.1, px: 1.6, m: 1.5, mt: 0 }),
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#fff",
            position: "sticky",
            top: 0,
            zIndex: 1000,
            height: "62px",
          }}
        >
          <Box
            sx={{
              mb: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PeopleAltIcon sx={{ fontSize: 28, color: "primary.main" }} />
              <Typography variant="h5" component="h1" sx={{ fontSize: "20px" }}>
                직원 관리
              </Typography>
            </Box>
            <Button
              sx={{
                height: "36px",
                borderRadius: "5px",
                mr: 1,
                width: "150px",
              }}
              variant="contained"
              color="primary"
              onClick={() => {
                setSelectedEmployee({
                  아이디: "",
                  프로필: "",
                  회원번호: 0,
                  유저번호: 0,
                  업체번호: Number(localStorage.getItem("CompanyNo")) || 0,
                  부서번호: 0,
                  부서명: "",
                  유저명: "",
                  직책명: "",
                  직책코드: "",
                  관리자여부: false,
                  수납관리자여부: false,
                  정산관리자여부: false,
                  통장조회가능여부: false,
                  발권담당자여부: false,
                  상태코드: "",
                  상태: "",
                  대표이메일: "",
                  국가번호: "",
                  대표전화: "",
                  영문성: "",
                  영문이름: "",
                  입사일: null,
                  생년월일: null,
                  외부콜센터여부: false,
                  성별: "",
                  성별코드: "",
                  기본주소: "",
                  상세주소: "",
                  우편번호: "",
                  재직여부: true,
                  관리자여부1: false,
                  알림수신: true,
                  
                });
                setModalOpen(true);
              }}
            >
              직원 등록
            </Button>
          </Box>
        </Paper>
        <Paper
          elevation={1}
          sx={{
            p: 3,
            m: 1.5,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#fff",
            minHeight: "calc(100vh - 116px)",
          }}
        >
          <Grid container spacing={3}>
            {employees.map((employee) => (
              <Grid item xs={12} sm={6} md={3} key={employee.유저번호}>
                <StyledCard onClick={() => handleEmployeeClick(employee)}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar
                        src={employee.프로필}
                        sx={{ width: 56, height: 56, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="h6">{employee.유저명}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {employee.부서명}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {employee.대표이메일}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {employee.대표전화}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Paper>
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "primary.main",
              color: "white",
              borderBottom: "1px solid #e0e0e0",
              px: 3,
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ px: 1 }}>
                {selectedEmployee?.유저번호 === 0 ? "직원 등록" : "직원 편집"}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  if (selectedEmployee) {
                    setPasswordChangeData(prev => ({...prev, userNo: selectedEmployee.유저번호}));
                    setPasswordChangeDialogOpen(true);
                  }
                }}
                sx={{
                  ml : 2,
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                비밀번호 변경
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={handleSave}
                variant="contained"
                color="primary"
                disabled={saving || !isFormValid()}
                sx={{ width: "100px", height: "40px" }}
              >
                {saving ? "저장 중..." : "저장"}
              </Button>
              <Button
                onClick={handleModalClose}
                variant="contained"
                color="secondary"
                disabled={saving}
                sx={{ width: "100px", height: "40px" }}
              >
                취소
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 6 }}>
            {selectedEmployee && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      // required
                      label={
                        <span>
                          아이디(이메일) <span style={{ color: '#FF0000' }}>*</span>
                        </span>
                      }
                      value={selectedEmployee.아이디}
                      onChange={(e) =>
                        handleEmployeeChange("아이디", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      // required
                      label={
                        <span>
                          한글이름 <span style={{ color: '#FF0000' }}>*</span>
                        </span>
                      }
                      value={selectedEmployee.유저명}
                      onChange={(e) =>
                        handleEmployeeChange("유저명", e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      // required
                      label={
                        <span>
                          이메일 <span style={{ color: '#FF0000' }}>*</span>
                        </span>
                      }
                      value={selectedEmployee.대표이메일}
                      onChange={(e) =>
                        handleEmployeeChange("대표이메일", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      // required
                      label={
                        <span>
                          휴대폰 <span style={{ color: '#FF0000' }}>*</span>
                        </span>
                      }
                      value={selectedEmployee.대표전화}
                      onChange={(e) =>
                        handleEmployeeChange("대표전화", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="성별"
                      value={selectedEmployee.성별}
                      onChange={(e) =>
                        handleEmployeeChange("성별", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedEmployee.재직여부}
                          onChange={(e) =>
                            handleEmployeeChange("재직여부", e.target.checked)
                          }
                        />
                      }
                      label="재직상태"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="영문성"
                      value={selectedEmployee.영문성}
                      onChange={(e) =>
                        handleEmployeeChange("영문성", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="영문이름"
                      value={selectedEmployee.영문이름}
                      onChange={(e) =>
                        handleEmployeeChange("영문이름", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="입사일"
                      type="date"
                      value={selectedEmployee.입사일 || ""}
                      onChange={(e) =>
                        handleEmployeeChange("입사일", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="생년월일"
                      type="date"
                      value={selectedEmployee.생년월일 || ""}
                      onChange={(e) =>
                        handleEmployeeChange("생년월일", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="주소"
                      value={selectedEmployee.기본주소}
                      onChange={(e) =>
                        handleEmployeeChange("기본주소", e.target.value)
                      }
                    />
                  </Grid>

                </Grid>
              </Box>
            )}
          </DialogContent>
        </Dialog>
        <Dialog
          open={passwordChangeDialogOpen}
          onClose={() => {
            setPasswordChangeDialogOpen(false);
            setPasswordChangeData({
              oldPassword: '',
              newPassword: '',
              confirmPassword: '',
              userNo: 0
            });
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "primary.main",
              color: "white",
            }}
          >
            비밀번호 변경
          </DialogTitle>
          <DialogContent sx={{ mt: 3 }}>
            <TextField
              fullWidth
              type="password"
              label="기존 비밀번호"
              value={passwordChangeData.oldPassword}
              onChange={(e) => setPasswordChangeData(prev => ({...prev, oldPassword: e.target.value}))}
              margin="normal"
            />
            <TextField
              fullWidth
              type="password"
              label="신규 비밀번호"
              value={passwordChangeData.newPassword}
              onChange={(e) => setPasswordChangeData(prev => ({...prev, newPassword: e.target.value}))}
              margin="normal"
            />
            <TextField
              fullWidth
              type="password"
              label="신규 비밀번호 재확인"
              value={passwordChangeData.confirmPassword}
              onChange={(e) => setPasswordChangeData(prev => ({...prev, confirmPassword: e.target.value}))}
              margin="normal"
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'flex-end', p: 2 }}>
            <Button
              onClick={() => {
                setPasswordChangeDialogOpen(false);
                setPasswordChangeData({
                  oldPassword: '',
                  newPassword: '',
                  confirmPassword: '',
                  userNo: 0
                });
              }}
            >
              닫기
            </Button>
            <Button
              variant="contained"
              onClick={handlePasswordChange}
              sx={{height: '32px'}}
            >
              확인
            </Button>
          </DialogActions>
        </Dialog>
        <AlertSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
        />
      </Box>
    </Box>
  );
};

export default EmployeeManagementPage;
